import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ocFetch, OcApiError } from "@/lib/oc-api";
import { OcAuthPageShell, ocAuthInputCls, ocAuthButtonCls } from "@/components/shared/OcAuthPageShell";

function ocApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof OcApiError) {
    const body = err.body as { error?: unknown } | undefined;
    if (body && typeof body.error === "string" && body.error) return body.error;
  }
  return fallback;
}

export function OcResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<"loading" | "valid" | "invalid" | "done">("loading");
  const [email, setEmail] = useState("");
  const [invalidReason, setInvalidReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setInvalidReason("This password reset link is missing its token.");
      return;
    }
    ocFetch<{ valid: boolean; email?: string; error?: string }>(
      `/tenant/auth/password-reset?token=${encodeURIComponent(token)}`
    )
      .then((res) => {
        if (res.valid && res.email) {
          setEmail(res.email);
          setStatus("valid");
        } else {
          setInvalidReason(res.error ?? "This password reset link is invalid or has expired.");
          setStatus("invalid");
        }
      })
      .catch((err: unknown) => {
        setInvalidReason(ocApiErrorMessage(err, "This password reset link is invalid or has expired."));
        setStatus("invalid");
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await ocFetch("/tenant/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err: unknown) {
      setError(ocApiErrorMessage(err, "Couldn't reset your password. The link may have expired."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OcAuthPageShell title="Choose a new password" subtitle="Reset your OnboardConnect password">
      {status === "loading" && (
        <p className="text-sm text-slate-500 text-center py-4">Checking your reset link…</p>
      )}

      {status === "invalid" && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
            {invalidReason}
          </div>
          <Link to="/forgot-password" className={`${ocAuthButtonCls} text-center`}>
            Request a new link
          </Link>
          <Link to="/login" className="block text-center text-sm font-medium text-oc-teal-600 hover:text-oc-teal-700">
            Back to sign in
          </Link>
        </div>
      )}

      {status === "done" && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 text-center">
          Password updated. Redirecting you to sign in…
        </div>
      )}

      {status === "valid" && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          <div className="space-y-1">
            <label htmlFor="oc-reset-email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="oc-reset-email"
              type="email"
              value={email}
              readOnly
              autoComplete="username"
              className={`${ocAuthInputCls} opacity-60 cursor-not-allowed`}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="oc-reset-password" className="block text-sm font-medium text-slate-700">
              New password
            </label>
            <input
              id="oc-reset-password"
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={ocAuthInputCls}
              placeholder="At least 8 characters"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="oc-reset-confirm" className="block text-sm font-medium text-slate-700">
              Confirm password
            </label>
            <input
              id="oc-reset-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={ocAuthInputCls}
              placeholder="Re-enter your password"
            />
          </div>
          <button type="submit" disabled={submitting} className={ocAuthButtonCls}>
            {submitting ? "Updating password…" : "Update password"}
          </button>
        </form>
      )}
    </OcAuthPageShell>
  );
}
